import { logger } from '../../../../utils/common/logger'
import { IIdentityAuth0Config } from '../adapter'

interface IWindow extends Window {
    auth0: any
}

interface IAuth0LoginOptions {
    authorizationParams?: IAuth0AuthorizationParams
}

interface IAuth0AuthorizationParams {
    commenting?: boolean
    screen_hint?: string
}

declare let window: IWindow

export class Auth0 {
    private auth0Client: any
    private auth0ClientUsingLocalStorageAndRefreshTokens: any

    constructor(private auth0Config: IIdentityAuth0Config) {}

    /**
     * Initialize Auth0 script and client
     * @param auth0Config
     */
    public async init() {
        if (!window.auth0) {
            return
        }

        // create Auth0 client
        this.auth0Client = await window.auth0.createAuth0Client({
            authorizationParams: {
                redirect_uri: `${window.location.origin}/auth0-redirect/?returnUrl=${window.location.href}`
            },
            clientId: this.auth0Config.clientId,
            domain: this.auth0Config.domain
        })

        // create Auth0 client, using localstorage cache and refresh tokens (workaround for safari and chrome on mobile)
        this.auth0ClientUsingLocalStorageAndRefreshTokens = await window.auth0.createAuth0Client({
            authorizationParams: {
                redirect_uri: `${window.location.origin}/auth0-redirect/?returnUrl=${window.location.href}`
            },
            cacheLocation: 'localstorage',
            clientId: this.auth0Config.clientId,
            domain: this.auth0Config.domain,
            useRefreshTokens: true,
        })

    }

    /**
     * Get id token
     */
    public async getIdToken(): Promise<string| undefined> {
        let idToken

        try {
            const claim = await this.auth0Client.getIdTokenClaims()
            if (claim) {
                idToken = claim.__raw
            } else {
                // Force Auth0 to obtain a new token
                await this.auth0ClientUsingLocalStorageAndRefreshTokens.getTokenSilently({
                    cacheMode: 'off'
                })
                const fallbackClaim = await this.auth0ClientUsingLocalStorageAndRefreshTokens.getIdTokenClaims()
                idToken = fallbackClaim.__raw
            }

        } catch(error) {
            logger('info', 'ID Token not found')
        }

        return idToken
    }

    /**
     * Redirects to Auth0 Universal Login sign in page
     *
     * @param isCommenting
     */
    public async signIn(isCommenting = false) {
        let options = {} as IAuth0LoginOptions

        if (isCommenting) {
            options = {
                authorizationParams: {
                    commenting: true
                }
            }
        }

        await this.auth0ClientUsingLocalStorageAndRefreshTokens.loginWithRedirect(options)
    }

    /**
     * Redirects to Auth0 Universal Login register page
     */
    public async register(isCommenting = false) {
        const options = {
            authorizationParams: {
                screen_hint: 'signup'
            }
        } as IAuth0LoginOptions

        if (isCommenting) {
            options.authorizationParams.commenting = true
        }

        await this.auth0ClientUsingLocalStorageAndRefreshTokens.loginWithRedirect(options)
    }

    /**
     * Sign out
     */
    public async signOut() {
        const options = {
            clientId: this.auth0Config.clientId,
            logoutParams: {
                returnTo: `${window.location.origin}/auth0-redirect/?state=logout&returnUrl=${window.location.href}`
            }
        }
        document.cookie = 'auth0_sub=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

        // Remove auth0_sub cookie set by Naviga
        const splitDomain = location.hostname.split('.')
        let lastTwoPartDomain = splitDomain.slice(-2).join('.') // tslint:disable-line: no-magic-numbers

        if (splitDomain.length > 2) { // tslint:disable-line: no-magic-numbers
            // add dot in front if more than 1 domain level
            lastTwoPartDomain = `.${lastTwoPartDomain}`
        }
        document.cookie = `auth0_sub=;domain=${lastTwoPartDomain};expires=Thu, 01 Jan 1970 00:00:00 UTC;`

        await this.auth0Client.logout(options)
    }
}
